import {
  Button,
  Checkbox,
  Divider,
  Group,
  Radio,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { useForm } from '@mantine/form'
import type { Patient, Reference, RelatedPerson } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import * as relatedPersons from '@seen/core/models/relatedPersons/index'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { SDRReviewType } from '@seen/core/models/tasks/sdr/utils'
import { createReference } from '@seen/core/references'
import { emptyFunction } from '@seen/core/utils'
import type { JSX } from 'react'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { SelectCreatable } from '#src/components/input/SelectCreatable.tsx'
import { SDRRequesterInput } from '#src/components/sdr/create/SDRRequesterInput.tsx'
import {
  adjustDateForSDRDeadlineAvoidingWeekends,
  isValidRequester,
  type SDRRequesterState,
} from '#src/components/sdr/create/utils.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

const MAX_TITLE_LENGTH = 100

type NewSDR = {
  title: string
  category: string | null
  description: string
  descriptionReadBack: boolean

  receiptMethod: string | null
  requester: SDRRequesterState | null

  reviewer: SDRReviewType
  receiptDate: Date
  requestedFulfillmentDate: Date | null

  explanationDocuments: string[]
  requestDocuments: string[]
}

type Props = {
  patient: Patient
  onCancel: () => void
  onCreate?: () => void
}

export function CreateSDR({ patient, onCancel, onCreate }: Props): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const form = useForm<NewSDR>({
    mode: 'uncontrolled',
    initialValues: {
      title: '',
      category: null,
      description: '',
      descriptionReadBack: false,

      receiptMethod: null,
      requester: null,

      reviewer: SDRReviewType.IDTGroupReview,
      receiptDate: new Date(),
      requestedFulfillmentDate: null,

      explanationDocuments: [],
      requestDocuments: [],
    },

    // functions will be used to validate values at corresponding key
    validate: {
      title: (value) =>
        value.trim().length > 0 && value.trim().length <= MAX_TITLE_LENGTH
          ? null
          : 'Please enter a title that is less than 100 characters',
      category: (value) =>
        value != null && value.trim().length > 0
          ? null
          : 'Please select a category',
      description: (value) =>
        value.trim().length > 0 ? null : 'Please enter a description',
      descriptionReadBack: (value) =>
        value
          ? null
          : 'Please confirm the description was read back to the requester',

      receiptMethod: (value) =>
        value != null ? null : 'Please select a receipt method',
      requester: (value) =>
        isValidRequester(value) ? null : 'Please select a requester',
      receiptDate: (value) => (value ? null : 'Please select a receipt date'),
    },
  })

  invariant(patient, 'Patient is required to create an SDR')

  const createMutation = useTransactionMutation()

  const idtReviewDueDate = adjustDateForSDRDeadlineAvoidingWeekends(
    form.getValues().receiptDate,
    'IDTReview'
  )
  const estimatedSDRDecisionDate = adjustDateForSDRDeadlineAvoidingWeekends(
    idtReviewDueDate,
    'DecisionNotification'
  )
  const estimatedSDRCompletionDate = adjustDateForSDRDeadlineAvoidingWeekends(
    estimatedSDRDecisionDate,
    'Effectuation'
  )

  return (
    <form
      onSubmit={form.onSubmit(
        (newSDR) => {
          createMutation.mutate(
            (transaction) => {
              const dueDate = adjustDateForSDRDeadlineAvoidingWeekends(
                newSDR.receiptDate,
                'IDTReview'
              )
              const patientRef = createReference(patient)

              invariant(newSDR.receiptMethod, 'Need to have receipt method')
              invariant(newSDR.category, 'Need to have category')
              invariant(newSDR.requester?.type, 'Need to have requester')

              let relatedPerson: RelatedPerson | undefined
              let requester: Reference<Patient | RelatedPerson>
              switch (newSDR.requester.type) {
                case 'participant':
                  // reference the patient themselves as the requester
                  requester = patientRef
                  break
                case 'relatedPerson':
                  requester = newSDR.requester.reference
                  break
                case 'newRelatedPerson':
                  relatedPerson = {
                    resourceType: 'RelatedPerson',
                    patient: patientRef,
                    name: [
                      {
                        given: [newSDR.requester.firstName],
                        family: newSDR.requester.lastName,
                        use: 'usual',
                      },
                    ],
                    telecom: [
                      {
                        system: 'phone',
                        value: newSDR.requester.phone,
                      },
                      {
                        system: 'email',
                        value: newSDR.requester.email,
                      },
                    ],
                    address: [
                      {
                        use: 'home',
                        line: [newSDR.requester.streetAddress],
                        city: newSDR.requester.city,
                        state: newSDR.requester.state,
                        postalCode: newSDR.requester.zip,
                        country: newSDR.requester.country,
                      },
                    ],
                    relationship: [
                      codeableConcept(
                        relatedPersons.makePersonRelationshipToPatientCoding(
                          newSDR.requester.relationship ?? ''
                        )
                      ),
                    ],
                  }
                  requester = transaction.create(relatedPerson)
                  break
              }

              transaction.create(
                SDR.buildSDR(
                  {
                    description: newSDR.description,
                    for: patientRef,
                    executionPeriod: {
                      start: newSDR.receiptDate.toISOString(),
                      end: dueDate,
                    },
                    requester,
                  },
                  {
                    title: newSDR.title,
                    category: newSDR.category,
                    receiptMethod: newSDR.receiptMethod,

                    requestedFulfillmentDate:
                      newSDR.requestedFulfillmentDate?.toISOString(),
                    explanationDocuments: newSDR.explanationDocuments,
                    requestDocuments: newSDR.requestDocuments,

                    reviewType: newSDR.reviewer,

                    // TODO implement documents
                    // explanationDocuments: string[]

                    // requestDocuments: string[]
                  }
                ).toFhirResource()
              )
            },
            {
              onSuccess: onCreate,
            }
          )
        },
        (err) => {
          console.error('Error:', err)
        }
      )}
    >
      <Stack p={0} gap={8}>
        <Group gap={8}>
          <TextInput
            label="Title"
            required
            placeholder='e.g. "Add new medication"'
            maxLength={MAX_TITLE_LENGTH}
            className="grow"
            key={form.key('title')}
            {...form.getInputProps('title')}
          />
          <SelectCreatable
            label="Category"
            required
            initialOptions={[
              'DME/Therapy',
              'Home Care',
              'Nutrition/Dentures',
              'Hearing Related',
              'Day Center Attendance',
              'Medical/Vision',
              'Transportation',
              'Respite',
              'Dental',
              'Medications',
            ]}
            allowDeselect={false}
            key={form.key('category')}
            {...form.getInputProps('category')}
          />
        </Group>

        <Textarea
          label="Description"
          description="Include the reasons why this SDR is being requested."
          required
          placeholder='e.g. "Patient needs a new medication to treat their condition"'
          key={form.key('description')}
          {...form.getInputProps('description')}
        />

        <Checkbox
          label="I have read the description back to the requestor for accuracy."
          key={form.key('descriptionReadBack')}
          {...form.getInputProps('descriptionReadBack')}
        />

        <Group gap={8}>
          {/* <Input.Wrapper className="grow" label="Attach Notes/Documentation">
            <Text>TODO</Text>
          </Input.Wrapper> */}

          <SelectCreatable
            label="Receipt method"
            required
            initialOptions={['Verbal', 'Phone', 'Written']}
            className="grow"
            allowDeselect={false}
            key={form.key('receiptMethod')}
            {...form.getInputProps('receiptMethod')}
          />
        </Group>

        <SDRRequesterInput
          patient={patient}
          required
          label="Requested by"
          key={form.key('requester')}
          {...form.getInputProps('requester')}
        />

        <Stack gap={8} className="rounded-md bg-gray-50 p-4">
          <Text className="font-semibold">Who is reviewing?</Text>
          <Group gap={8} className="items-start">
            <Stack gap={8} className="grow">
              <Radio.Group
                key={form.key('reviewer')}
                {...form.getInputProps('reviewer')}
              >
                <Radio
                  label="IDT Review"
                  value={SDRReviewType.IDTGroupReview}
                  className="my-1"
                />
                {/* TODO: disable direct approval after SRR */}
                <Radio
                  label="Myself (Direct Approval)"
                  value={SDRReviewType.DirectApproval}
                  className="my-1"
                />
              </Radio.Group>

              <Group className="grow items-end">
                <DateTimePicker
                  label="Receipt date"
                  required
                  valueFormat="DD MMM YYYY hh:mm A"
                  key={form.key('receiptDate')}
                  {...form.getInputProps('receiptDate')}
                  className="grow"
                  onChange={(date) => {
                    if (date == null) {
                      return
                    }
                    form.setFieldValue('receiptDate', date)
                  }}
                />
                <Button
                  className="flex shrink-0 grow-0"
                  onClick={() => {
                    form.setFieldValue('receiptDate', new Date())
                  }}
                  variant="default"
                >
                  Now
                </Button>
              </Group>

              {/* TODO: re-enable after SRR */}
              {/* <DateTimePicker
                label="Requested fulfillment by"
                name="Requested fulfillment by"
                valueFormat="DD MMM YYYY hh:mm A"
                key={form.key('requestedFulfillmentDate')}
                {...form.getInputProps('requestedFulfillmentDate')}
                placeholder="Optional date to request fulfillment by"
              /> */}
            </Stack>
            <Divider orientation="vertical" mx="lg" />
            <Stack className="grow">
              <Stepper
                active={1}
                onStepClick={emptyFunction}
                orientation="vertical"
                size="xs"
                iconSize={28}
              >
                <Stepper.Step
                  label={
                    <Text size="sm" className="font-semibold">
                      New SDR
                    </Text>
                  }
                  description={
                    <StepperDateDescription
                      description="SDR created"
                      date={form.getValues().receiptDate.toISOString()}
                    />
                  }
                  className="cursor-default"
                />
                <Stepper.Step
                  label={
                    <Text size="sm" className="font-semibold">
                      Review
                    </Text>
                  }
                  description={
                    <StepperDateDescription
                      description="IDT review due"
                      date={idtReviewDueDate}
                    />
                  }
                  className="cursor-default"
                />
                <Stepper.Step
                  label={
                    <Text size="sm" className="font-semibold">
                      Decision
                    </Text>
                  }
                  description={
                    <StepperDateDescription
                      description="Disposition outcome due"
                      date={estimatedSDRDecisionDate}
                    />
                  }
                  className="cursor-default"
                />
                <Stepper.Step
                  label={
                    <Text size="sm" className="font-semibold">
                      Effectuation
                    </Text>
                  }
                  description={
                    <StepperDateDescription
                      description="Service fulfillment due"
                      date={estimatedSDRCompletionDate}
                    />
                  }
                  className="cursor-default"
                />
              </Stepper>
            </Stack>
          </Group>
        </Stack>

        <div className="flex grow flex-row items-center justify-end gap-2">
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={createMutation.isPending}
            disabled={isReadOnly}
          >
            Submit
          </Button>
        </div>
      </Stack>
    </form>
  )
}

function StepperDateDescription({
  description,
  date,
}: {
  description: string
  date: string
}): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{description}</span>
      <span className="text-xs text-black">
        <DateDisplay format="eeee MM/dd/yy" element="span" value={date} />
      </span>
    </div>
  )
}
